import { Button } from '@Components/button/button'
import { DateTime } from 'luxon'
import { useCallback } from 'react'

import AppleLogo from '@Assets/logos/apple.svg'
import GoogleLogo from '@Assets/logos/google.svg'
import OutlookLogo from '@Assets/logos/outlook.svg'
import { ReactSVG } from 'react-svg'

import './calendar-event-creator.scss'
import { SUPPORTED_CALENDARS } from './types'

const logoByComp: Record<SUPPORTED_CALENDARS, string> = {
  [SUPPORTED_CALENDARS.GOOGLE]: GoogleLogo,
  [SUPPORTED_CALENDARS.APPLE]: AppleLogo,
  [SUPPORTED_CALENDARS.OUTLOOK]: OutlookLogo,
}

export type CalendarEventProps = {
  calendarTypes: SUPPORTED_CALENDARS[]
  title: string
  details: string
  location: string
  startTime: DateTime
  endTime: DateTime
}

export const CalendarEventCreate = ({
  calendarTypes,
  title,
  details,
  location,
  startTime,
  endTime,
}: CalendarEventProps) => {
  const calTitle = encodeURIComponent(title)
  const calDetails = encodeURIComponent(details)
  const calLocation = encodeURIComponent(location)

  const createAppleCalendarEvent = useCallback(() => {
    const formatICS = (dt: DateTime) => dt.toUTC().toFormat("yyyyLLdd'T'HHmmss'Z'")

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      `SUMMARY:${title}`,
      `DESCRIPTION:${details}`,
      `LOCATION:${location}`,
      `DTSTART:${formatICS(startTime)}`,
      `DTEND:${formatICS(endTime)}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n')

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = 'event.ics'
    link.click()
    URL.revokeObjectURL(url)
  }, [title, details, location, startTime, endTime])

  const createGoogleCalendarEvent = useCallback(() => {
    const start = startTime.toUTC().toFormat("yyyyLLdd'T'HHmmss'Z'")
    const end = endTime.toUTC().toFormat("yyyyLLdd'T'HHmmss'Z'")
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${calTitle}&details=${calDetails}&location=${calLocation}&dates=${start}/${end}`

    window.open(url, '_blank')
  }, [calTitle, calDetails, calLocation, startTime, endTime])

  const createOutlookLink = useCallback(() => {
    const start = startTime.toISO({ suppressMilliseconds: true })
    const end = endTime.toISO({ suppressMilliseconds: true })
    const url = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${calTitle}&body=${calDetails}&startdt=${start}&enddt=${end}&location=${calLocation}`
    window.open(url, '_blank')
  }, [calTitle, calDetails, calLocation, startTime, endTime])

  const clickHandler = useCallback(
    (calType: SUPPORTED_CALENDARS) => {
      switch (calType) {
        case SUPPORTED_CALENDARS.APPLE:
          return createAppleCalendarEvent()
        case SUPPORTED_CALENDARS.GOOGLE:
          return createGoogleCalendarEvent()
        case SUPPORTED_CALENDARS.OUTLOOK:
          return createOutlookLink()
      }
    },
    [createGoogleCalendarEvent, createOutlookLink, createAppleCalendarEvent],
  )

  const buttons = calendarTypes.map((cal) => {
    return (
      <Button className="calendar-icon-button" key={cal} onClick={() => clickHandler(cal)}>
        <ReactSVG
          src={logoByComp[cal]}
          beforeInjection={(svg) => {
            svg.setAttribute('width', '32')
            svg.setAttribute('height', '32')
          }}
        />
      </Button>
    )
  })

  return <section className="calendar-icon-group">{buttons}</section>
}
